import {GridRenderCellParams} from '@mui/x-data-grid';
import {AurDataGridColDef} from '../types/types';
import {t} from 'i18next';
import {AurLightTooltip} from '../components/common/aur-light-tooltip/aur-light-tooltip';
import {UserRoleCode} from '../types/user-role-codes/roleCodes';
import {Option} from './../components/common/aur-select/AurSelect';

export interface IGroups {
  id: string;
  name: string;
}

export interface RowData {
  userClientId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: IGroups;
  status: string;
  clientName: string;
  permissionGroupName: string;
  projectName: string;
  projectCount: string;
}

export interface MemberStats {
  total: number;
  invited: number;
  active: number;
  inactive: number;
}

export type UserStatusUpdate = {userId: string; status: string};

/*
Column name for user management table
*/

export const MANAGE_USER_TABLE_WIDTH = 1292;

export const getUserManagementColumns = (): AurDataGridColDef[] => [
  {
    field: 'name',
    headerName: t('userManagement.name'),
    minWidth: 200,
    flex: 200 / MANAGE_USER_TABLE_WIDTH,
    sortable: true,
    renderCell: (params) => (
      <span>
        {params?.row?.lastName}, {params?.row?.firstName}
      </span>
    ),
  },
  {
    field: 'email',
    headerName: t('userManagement.emailAddress'),
    minWidth: 200,
    flex: 200 / MANAGE_USER_TABLE_WIDTH,
    sortable: true,
  },
  {
    field: 'roleName',
    headerName: t('userManagement.role'),
    minWidth: 128,
    flex: 128 / MANAGE_USER_TABLE_WIDTH,
    sortable: true,
  },
  {
    field: 'status',
    headerName: t('userManagement.status'),
    minWidth: 106,
    flex: 106 / MANAGE_USER_TABLE_WIDTH,
    sortable: true,
  },
  {
    field: 'clientName',
    headerName: t('userManagement.client'),
    minWidth: 128,
    flex: 128 / MANAGE_USER_TABLE_WIDTH,
    sortable: false,
  },
  {
    field: 'clientPermGroup',
    headerName: t('userHomePage.clientPermGroup'),
    minWidth: 156,
    flex: 156 / MANAGE_USER_TABLE_WIDTH,
    sortable: false,
  },
  {
    field: 'firstProjectPermName',
    headerName: t('userHomePage.projectPermGroup'),
    minWidth: 156,
    flex: 156 / MANAGE_USER_TABLE_WIDTH,
    sortable: false,
    renderCell: displayCellWithPlus,
  },
  {
    field: 'firstProjectName',
    headerName: t('userManagement.projects'),
    minWidth: 140,
    flex: 140 / MANAGE_USER_TABLE_WIDTH,
    sortable: false,
    renderCell: displayCellWithPlus,
  },
];

export const getStatusUpdatesFromResponse = (response: {
  data?: {data?: unknown};
}): UserStatusUpdate[] => {
  const payload = response?.data?.data;
  const payloadValue = Array.isArray((payload as {value?: unknown})?.value)
    ? (payload as {value: unknown[]}).value
    : [];
  const raw = Array.isArray(payload) ? payload : payloadValue;
  return raw.map((u: {userId: string; status: string}) => ({
    userId: String(u.userId),
    status: u.status,
  }));
};

export const statusMatchesTab = (status: string, tab: string): boolean =>
  tab === 'all' || status.toLowerCase() === tab.toLowerCase();

export const memberStatKey = (
  status: string
): keyof Pick<MemberStats, 'active' | 'inactive' | 'invited'> | null => {
  const normalized = status.toLowerCase();
  if (normalized === 'active') return 'active';
  if (normalized === 'inactive') return 'inactive';
  if (normalized === 'invited') return 'invited';
  return null;
};

/* 
Function to generate accessibility properties for tab elements
*/
export const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
});

let context: CanvasRenderingContext2D | null = null;
const getCanvasContext = () => {
  if (!context) {
    context = document.createElement('canvas').getContext('2d');
  }
  return context;
};

interface TextFitOptions {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string | number;
  separator?: string;
}

const DEFAULT_OPTIONS: Required<TextFitOptions> = {
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: '400',
  separator: ', ',
};

export const getVisibleItemsForWidth = (
  items: string[],
  containerWidth: number,
  options: TextFitOptions = {}
) => {
  const context = getCanvasContext();
  if (!context || items.length === 0) {
    return {
      visibleItems: [],
      visibleText: '',
      remainingCount: items.length,
    };
  }

  const {fontFamily, fontSize, fontWeight, separator} = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  context.font = `${fontWeight} ${fontSize} ${fontFamily}`;

  // Always include first item
  const visibleItems = [items[0]];

  for (let i = 1; i < items.length; i++) {
    const candidate = [...visibleItems, items[i]].join(separator);
    if (context.measureText(candidate).width > containerWidth) {
      break;
    }
    visibleItems.push(items[i]);
  }

  return {
    visibleItems,
    visibleText: visibleItems.join(separator),
    isFullyFitOnSpace:
      context.measureText(visibleItems.join(separator)).width <= containerWidth,
    remainingCount: items.length - visibleItems.length,
  };
};

export const displayCellWithPlus = (params: GridRenderCellParams) => {
  let content: string[] = [];
  switch (params.field) {
    case 'firstProjectPermName':
      {
        content = (params?.row?.projectPermGroup ?? '')
          .split(',')
          .filter(Boolean);
      }
      break;
    case 'firstProjectName':
      {
        content = (params?.row?.projects ?? '').split(',').filter(Boolean);
      }
      break;
    default:
      break;
  }

  // 12 is left padding of cell
  // 32 is space for show +1 at end of cell
  const contentWidth = params.colDef.computedWidth - (12 + 32);

  if (content.length === 1) return <span>{content[0]}</span>;

  if (content.length > 0) {
    const result = getVisibleItemsForWidth(content, contentWidth);
    return (
      <span className="plus-name-cell-text">
        <AurLightTooltip title={result.visibleText}>
          <span className="data-wrap" style={{maxWidth: contentWidth}}>
            {result.visibleText}
          </span>
        </AurLightTooltip>
        {result.remainingCount > 0 && result.isFullyFitOnSpace && (
          <span className="seperator-comma">{','}</span>
        )}
        {result.remainingCount > 0 && (
          <span className="plus-count-text">
            {` ${result.remainingCount}+`}
          </span>
        )}
      </span>
    );
  }
  return <span />;
};

export const getFilteredRoleOptionList = (
  primaryRole: string,
  roleOptionList: Option[]
) => {
  switch (primaryRole) {
    case UserRoleCode.systemAdminUserRole:
    case UserRoleCode.clientAdminUserRole:
      return roleOptionList;
    case UserRoleCode.consilioUserRole:
      return roleOptionList.filter((item) =>
        [UserRoleCode.consilioUserRole, UserRoleCode.clientUserRole]
          .map((value) => value.toString())
          .includes(item?.roleCode ?? '')
      );
    case UserRoleCode.clientUserRole:
      return roleOptionList.filter((item) =>
        [UserRoleCode.clientUserRole]
          .map((value) => value.toString())
          .includes(item?.roleCode ?? '')
      );
    default:
      return roleOptionList;
  }
};
